import axios from 'axios';

interface APIError<T = unknown> {
  code: number;
  data?: T;
  message?: string;
  msg?: string;
  logId?: string;
  api?: string;
}

export const isAPIError = (err: unknown): err is APIError =>
  (err as Partial<APIError>).code !== undefined ||
  Boolean((err as Partial<APIError>)?.message) ||
  Boolean((err as Partial<APIError>)?.msg);

export const getErrorDetail = (error: unknown) => {
  let errorMessage: string | undefined;
  let errorCode = 0;
  let api = '';
  let logId: string | undefined;
  let isAxiosError = false;
  if (axios.isAxiosError(error)) {
    errorMessage = error.message;
    errorCode = error.response?.status ?? 0;
    api = error.config?.url ?? '';
    logId = error.response?.headers?.['x-tt-logid'];
    isAxiosError = true;
  } else if (isAPIError(error)) {
    errorCode = error?.code;
    errorMessage = error?.message ?? error?.msg;
    logId = error?.logId;
    api = error?.api ?? '';
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  return {
    errorMessage,
    errorCode,
    api,
    logId,
    isAxiosError,
  };
};

export const getCookies = (...keys: string[]): Record<string, string> => {
  const allCookies = document.cookie
    .split('; ')
    .map(item => item.split('='))
    .reduce((cookies: Record<string, string>, item) => {
      cookies[item[0]] = item[1];
      return cookies;
    }, {});
  return keys.reduce((cookies: Record<string, string>, key) => {
    cookies[key] = allCookies[key];
    return cookies;
  }, {});
};

export function getQueryString<T = string>(name: string) {
  const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`, 'i');
  const r = reg.exec(window.location.search.substr(1));
  if (r !== null && r !== undefined) {
    return decodeURIComponent(r[2]) as unknown as T;
  }
  return null;
}

export const setQueryString = (obj: unknown) => {
  const params = new URLSearchParams(window.location.search);
  if (typeof obj === 'object') {
    for (const key in obj) {
      const value = (obj as any)[key];
      if (value === '' || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
  }
  window.history.replaceState(
    {},
    '',
    decodeURIComponent(`${window.location.pathname}?${params.toString()}`),
  );
};

export const isSafari = () =>
  navigator.userAgent.includes('Safari') &&
  !navigator.userAgent.includes('Chrome');

export function isPWA() {
  // when in fullscreen mode at browser tab, displayMode is also fullscreen
  return window.matchMedia(`(display-mode: standalone)`).matches;
}

export const isPlainObject = (data: any) =>
  Object.prototype.toString.call(data) === '[object Object]';

export const noop = () => {
  /* do nothing */
};

export function toHump(name: string) {
  return name.replace(/_(\w)/g, (_, letter) => letter.toUpperCase());
}

export function objectKeyToHump(data: any[]): any[];
export function objectKeyToHump(data: Record<string, any>): Record<string, any>;
export function objectKeyToHump(data: Record<string, any> | any[]) {
  if (Array.isArray(data)) {
    return data.map(v => objectKeyToHump(v));
  }
  if (isPlainObject(data)) {
    return Object.keys(data).reduce((pre, cur) => {
      const val = objectKeyToHump(data[cur]);
      return {
        ...pre,
        [toHump(cur)]: val,
      };
    }, {});
  }
  return data;
}

type Fn<T extends any[], R> = (...args: T) => R;

export const curry =
  (fn: Fn<any, any>, ...rest: any[]) =>
  (...args: any[]) =>
    fn(...rest.concat(args));

export function downloadFile(filePath: string, fileName?: string) {
  const link = document.createElement('a');
  link.href = filePath;
  if (fileName === undefined || fileName === '') {
    link.download = filePath.substr(filePath.lastIndexOf('/') + 1);
  } else {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Promisify setTimeout
 *
 * @param delay - millisecond
 * @returns promise
 */
export function setTimeoutPromise(delay: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, delay));
}

export function promisePoller<T>(
  task: () => Promise<T>,
  exit: (res: T) => boolean,
  interval = 1500,
  times = 1,
  ignoreError = false,
): Promise<T> {
  return task().then(
    (res: T) => {
      if (exit(res)) {
        return res;
      }
      if (times > 1) {
        return setTimeoutPromise(interval).then(() =>
          promisePoller(task, exit, interval, times - 1, ignoreError),
        );
      }
      const _res = res as unknown as Error;
      throw _res;
    },
    err => {
      if (ignoreError && times > 1) {
        return setTimeoutPromise(interval).then(() =>
          promisePoller(task, exit, interval, times - 1, ignoreError),
        );
      }
      throw err;
    },
  );
}

export const safeParse = (str: string, defaultVal: any = {}): any => {
  if (!str) {
    return defaultVal;
  }
  try {
    return JSON.parse(str);
  } catch (e) {
    return defaultVal;
  }
};

declare global {
  interface String {
    test: (str: string) => boolean;
  }
}

/**
 * background: because garfish-slardar-plugin request match property with RegExp type, while jupiter resolve config
 * with JSON.stringify method, which prevents the config of regular expressions.
 * In garfish-slardar-plugin, it will use reg.test to judge if the url match submodule, in order to support this,
 * we need to add test function to string property
 */
export function hackStringTestFunction() {
  // eslint-disable-next-line no-extend-native
  String.prototype.test = function (str: string) {
    const reg = new RegExp(this.toString());
    return reg.test(str);
  };
}

type PrefixReturnType = (str: string) => string;

export const prefix =
  (p: string): PrefixReturnType =>
  (str: string) =>
    p.concat(str);

export * from './input-filter';

export { default as ecLocalStorage } from './local-storage';
export { default as ecSessionStorage } from './session-storage';
export { default as ErrorHandler } from './error-handler';

export * from './history';
export * as imUtils from './im';
export { gotoImWeb } from './im';

export * from './starling-localforage';
export * as PhoneAreaCode from './phone-area-code';
export * from './path';
export * from './seller-product';
export * as DataCenterUtils from './data-center';
export * from './tea';
export * from './cookie-banner-language-adaptor';
export * from './reverse-map';
